<script setup lang="ts">
import { Heart, Home, Mail, Sparkles, Star } from 'lucide-vue-next';
import type { Friend } from '@/types/domain';

defineProps<{
  friend: Friend;
  care?: 'quiet' | 'warm' | 'glowing';
  hasFlyingLetter?: boolean;
}>();
</script>

<template>
  <button
    class="friend-house focus-ring group relative min-h-72 overflow-hidden rounded-[32px] border border-white/70 bg-white/75 p-4 text-heart-ink shadow-soft transition hover:-translate-y-1 active:scale-[0.98]"
    :class="{
      'friend-house-warm': care === 'warm',
      'friend-house-glowing': care === 'glowing'
    }"
    :style="{ '--friend-house-color': friend.houseColor ?? '#bae6fd' }"
    type="button"
    :aria-label="`${friend.displayName}s Haus oeffnen`"
  >
    <span v-if="hasFlyingLetter" class="friend-letter-flight" aria-hidden="true">
      <Mail class="h-6 w-6" />
    </span>

    <span class="friend-stars" aria-hidden="true">
      <Star class="friend-star friend-star-a h-4 w-4 fill-yellow-200 text-yellow-400" />
      <Sparkles class="friend-star friend-star-b h-5 w-5 fill-sky-100 text-sky-500" />
      <Star v-if="care === 'glowing'" class="friend-star friend-star-c h-4 w-4 fill-rose-100 text-heart-coral" />
    </span>

    <span class="friend-tree" :class="{ 'friend-tree-grown': care === 'glowing' }" aria-hidden="true">
      <span class="friend-tree-trunk" />
      <span class="friend-tree-crown" />
    </span>

    <span class="friend-home-wrap">
      <span class="friend-home-roof" />
      <span class="friend-home-body">
        <Home class="h-16 w-16 fill-current text-sky-700" aria-hidden="true" />
        <span class="friend-home-window" />
      </span>
    </span>

    <span class="friend-flowers" aria-hidden="true">
      <span class="friend-flower friend-flower-a" />
      <span class="friend-flower friend-flower-b" />
      <span v-if="care !== 'quiet'" class="friend-flower friend-flower-c" />
      <span v-if="care === 'glowing'" class="friend-flower friend-flower-d" />
    </span>

    <span v-if="care !== 'quiet'" class="friend-hearts" aria-hidden="true">
      <Heart class="friend-heart friend-heart-a h-4 w-4 fill-heart-coral text-heart-coral" />
      <Heart class="friend-heart friend-heart-b h-5 w-5 fill-rose-300 text-rose-300" />
    </span>

    <span class="absolute inset-x-4 bottom-4 rounded-2xl bg-white/85 px-4 py-3 text-center text-2xl font-black shadow-sm md:text-3xl">
      {{ friend.displayName }}
    </span>
  </button>
</template>
